/** @jsx jsx */
import { useCallback, useEffect, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { Link } from 'gatsby';

import Button from '@atlaskit/button';
import MenuIcon from '@atlaskit/icon/glyph/menu';
import CloseIcon from '@atlaskit/icon/glyph/editor/close';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { useLocation } from '@reach/router';

import {
  hideOnDesktopNavigationStyles,
  gutterStyles,
  Logo,
  LogoIcon,
} from '@af/design-system-docs-ui';
import { media } from '@atlaskit/primitives/responsive';
import Drawer from '@atlaskit/drawer';
import Box from '@atlaskit/ds-explorations/box';
import Inline from '@atlaskit/primitives/inline';
import { token } from '@atlaskit/tokens';

import Settings from './settings';
import SideNavigation, { SideNavigationItem } from './side-navigation';

const fullLogoStyles = css({
  display: 'none',
  maxHeight: '1.25rem',
  marginTop: token('space.025', '2px'), // Counterbalance top-heavy logo

  [media.above.sm]: {
    display: 'block',
  },
});

const iconLogoStyles = css({
  display: 'flex',

  [media.above.sm]: {
    display: 'none',
  },
});

const headerStyles = css({
  display: 'grid',
  gridTemplateAreas: 'hamburger logo search',
  gridTemplateColumns: '7.5rem auto 7.5rem',
  alignItems: 'center',
  height: '100%',
  background: token('elevation.surface', '#FFFFFF'),
  borderBottom: `1px solid ${token('color.border', '#091E4224')}`,
});

const hamburgerStyles = css({
  // Pull left to align button icon with left gutter
  marginLeft: `calc(-1 * ${token('space.100', '8px')})`,
});

const drawerHeaderStyles = css({
  paddingInline: token('space.150', '12px'),
  position: 'sticky',
  top: 0,
  background: token('elevation.surface', '#FFFFFF'),
  zIndex: 1,
});

type MobileHeaderProps = {
  navigation: SideNavigationItem[];
  onSearchOpen?(): void;
};

const MobileHeader = ({ navigation, onSearchOpen }: MobileHeaderProps) => {
  const { pathname } = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleSidebarOpen = useCallback(() => {
    setIsSidebarOpen(true);
  }, []);

  const handleSidebarClose = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  // Close sidebar drawer when navigation changed.
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [pathname]);

  return (
    <header css={[headerStyles, gutterStyles, hideOnDesktopNavigationStyles]}>
      <div css={hamburgerStyles}>
        <Button
          appearance="subtle"
          onClick={handleSidebarOpen}
          iconBefore={<MenuIcon size="medium" label="" />}
          label="Open navigation"
        />
        <Drawer
          onClose={handleSidebarClose}
          isOpen={isSidebarOpen}
          // eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
          overrides={{
            Sidebar: {
              component: () => null,
            },
          }}
        >
          <div css={drawerHeaderStyles}>
            <Box justifyContent="end">
              <Button
                appearance="subtle"
                onClick={handleSidebarClose}
                iconBefore={<CloseIcon label="Close navigation" />}
              />
            </Box>
          </div>
          <SideNavigation device="mobile" navigation={navigation} />
        </Drawer>
      </div>

      <Box justifyContent="center">
        <Link to="/">
          <div css={fullLogoStyles}>
            <Logo id="mobile-header-logo" />
          </div>
          <div css={iconLogoStyles}>
            <LogoIcon id="mobile-header-icon" />
          </div>
        </Link>
      </Box>

      <Inline space="space.100" alignInline="end">
        <Button
          appearance="subtle"
          onClick={onSearchOpen}
          iconBefore={<SearchIcon size="medium" label="" />}
          label="Open search"
        />
        <Settings />
      </Inline>
    </header>
  );
};

export default MobileHeader;
